import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, ClickAwayListener, Divider, Popper, Skeleton, Typography } from '@mui/material';
import PresenceAvatarGroup from '@sharedCR/common/PresenceAvatarGroup';
import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import PopoverCDS from '@sharedCR/coreDesignSystem/PopoverCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { TAG_WIDGET_IDS, WidgetType } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { useBreakpoints } from 'hooks/useBreakpoints';
import useOverflowCount from 'hooks/useOverflowCount';
import usePopover from 'hooks/usePopover';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { formatDynamicValue } from 'utils/dynamicValues';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  selectAusSummary,
  selectIsApplicationHeaderViewLoading,
  selectIsAusSubmitting,
  selectIsAusSwitching,
  selectIsEmptyCommonInfoLoanApplication,
  selectLoanHeaderApplicationData,
  selectLoanMilestoneStatusName,
  selectLoanNumber,
  selectPrimaryLoanHeaderView,
} from 'store/loanApplications/selectors';

import AusComparison from '../../AusComparison';
import HeaderGrid from '../../HeaderGrid';
import { columnStyles } from '../../HeaderGrid/HeaderGrid.styles';
import { flattenHeaderColumns, getGridRowCount } from '../../HeaderGrid/HeaderGrid.utils';
import HeaderGridCell from '../../HeaderGrid/HeaderGridCell';
import { STATUS_FIELD_NAME } from '../../HeaderItemValue/HeaderItemValue.constants';
import { ausRelatedFields } from '../../LoanHeaders.constants';
import { LoanHeadersTestIds } from '../../LoanHeaders.testIds';
import { getIsAusLoadingStatus } from '../../LoanHeaders.utils';
import MilestoneRoadmapWidget from '../../MilestoneRoadmapWidget';
import LoanInfoHeaderItem from '../../RedesignedLoanInfoHeader/LoanInfoHeaderItem';
import HeaderFieldValue from '../HeaderFieldValue';

import {
  MOBILE_MAX_HEADER_ITEMS,
  popperModifiers,
  SCROLL_DEBOUNCE_MS,
} from './HeaderViewList.constants';
import {
  ausFieldClickableStyles,
  containerStyles,
  detailedSummaryGridStyles,
  detailedSummarySectionStyles,
  detailedSummaryTitleStyles,
  identityBorrowerStyles,
  identityDividerStyles,
  identityItemStyles,
  identityLoanNumberStyles,
  identityRowStyles,
  identitySkeletonStyles,
  itemNameStyles,
  itemStyles,
  measureContainerStyles,
  metricsContainerStyles,
  metricsRowStyles,
  moreButtonWrapperStyles,
  overflowPopoverContentStyles,
  popoverDividerStyles,
  popperStyles,
  skeletonStyles,
} from './HeaderViewList.styles';
import {
  filterRenderableHeaderColumns,
  getHeaderItemKey,
  getIsLoanNumberField,
  getMobileHeaderColumns,
  partitionPrimaryHeaderColumns,
} from './HeaderViewList.utils';

const ausComparisonPopperId = 'aus-comparison-popper';
const popoverAnchorOrigin = { vertical: 'bottom', horizontal: 'right' };
const popoverTransformOrigin = { vertical: 'top', horizontal: 'right' };

const defaultAssignedUsers = [];
const defaultSecondaryLoanHeaderView = [];

const HeaderViewList = ({
  assignedUsers = defaultAssignedUsers,
  secondaryLoanHeaderView = defaultSecondaryLoanHeaderView,
}) => {
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const { applicationId } = useParams();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopperHover, setIsPopperHover] = useState(false);

  const {
    anchorEl: overflowAnchorEl,
    onSetAnchorElement: onOpenOverflowPopover,
    onClearAnchorElement: onCloseOverflowPopover,
  } = usePopover();

  const timeoutRef = useRef(null);

  const { isViewAllowed: isReportsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.UNDERWRITING_REPORTS),
  );
  const isEmptyCommonInfoLoanApplication = useSelector(selectIsEmptyCommonInfoLoanApplication);
  const isAusSubmitting = useSelector(selectIsAusSubmitting);
  const primaryLoanHeaderView = useSelector(selectPrimaryLoanHeaderView);
  const isHeaderViewLoading = useSelector(selectIsApplicationHeaderViewLoading);
  const ausSummary = useSelector(selectAusSummary);
  const isAusSwitching = useSelector(selectIsAusSwitching);
  const loanHeaderApplicationData = useSelector(selectLoanHeaderApplicationData);
  const loanNumber = useSelector(selectLoanNumber);
  const milestoneStatusName = useSelector(selectLoanMilestoneStatusName);
  const model = useMemo(() => loanHeaderApplicationData || {}, [loanHeaderApplicationData]);

  const { isMobile } = useBreakpoints();

  const {
    measureRef,
    containerRef: visibleContainerRef,
    maxVisibleItems,
    gap,
  } = useOverflowCount();

  const visibleColumns = useMemo(
    () =>
      filterRenderableHeaderColumns({
        columns: primaryLoanHeaderView,
        isApplicationLoaded: !isEmptyCommonInfoLoanApplication,
        isReportsAllowed,
        hasAssignedUsers: !!assignedUsers?.length,
      }),
    [primaryLoanHeaderView, isEmptyCommonInfoLoanApplication, isReportsAllowed, assignedUsers],
  );

  const { identityItems, metricColumns } = useMemo(
    () => partitionPrimaryHeaderColumns(visibleColumns, loanNumber),
    [visibleColumns, loanNumber],
  );

  const measureRowCount = useMemo(() => getGridRowCount(metricColumns), [metricColumns]);

  const measureColumnSx = useMemo(
    () => columnStyles({ rowCount: measureRowCount, hasEqualColumns: false }),
    [measureRowCount],
  );

  const { displayedColumns, hasOverflow } = useMemo(() => {
    if (isMobile) {
      return getMobileHeaderColumns(metricColumns, MOBILE_MAX_HEADER_ITEMS);
    }

    const desktopHasOverflow = metricColumns.length > maxVisibleItems;

    return {
      displayedColumns: desktopHasOverflow
        ? metricColumns.slice(0, maxVisibleItems)
        : metricColumns,
      hasOverflow: desktopHasOverflow,
    };
  }, [isMobile, metricColumns, maxVisibleItems]);

  useEffect(() => {
    if (!isHeaderViewLoading && !isAusSwitching) {
      return;
    }

    setAnchorEl(null);
  }, [isAusSwitching, isHeaderViewLoading]);

  const onMouseEnter = useCallback(e => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setAnchorEl(e.currentTarget);
  }, []);

  const onMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, SCROLL_DEBOUNCE_MS);
  }, []);

  const onAusFieldClick = useCallback(
    e => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const target = e.currentTarget;

      if (overflowAnchorEl) {
        const rect = target.getBoundingClientRect();
        const virtualAnchor = { getBoundingClientRect: () => rect };
        onCloseOverflowPopover();
        setAnchorEl(virtualAnchor);

        return;
      }

      setAnchorEl(prev => (prev === target ? null : target));
    },
    [overflowAnchorEl, onCloseOverflowPopover],
  );

  const onPopperClickAway = useCallback(() => {
    setAnchorEl(null);
    setIsPopperHover(false);
  }, []);

  const onPopperEnter = useCallback(() => {
    setIsPopperHover(true);
  }, []);

  const onPopperLeave = useCallback(() => {
    setIsPopperHover(false);
  }, []);

  const renderHeaderItem = useCallback(
    (item, { withAusComparison = false } = {}) => {
      const {
        name,
        value,
        dataType,
        normalizedName,
        dynamicFieldName,
        dynamicFieldId,
        showLabel,
        hasPendingChange,
        changeKind,
      } = item;
      const shouldShowLabel = showLabel !== false;
      const isLoanRolesWidget = dynamicFieldId === WidgetType.LOAN_ROLES.id;

      if (isLoanRolesWidget) {
        return (
          !!assignedUsers?.length && (
            <Box className="gap-12" key={item.id || name} sx={itemStyles}>
              {shouldShowLabel && (
                <Typography
                  color="secondary"
                  data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_LOAN_INFO_HEADER_KEY_LABEL}
                  sx={itemNameStyles}
                  variant="regular14"
                >
                  {name}
                </Typography>
              )}
              <PresenceAvatarGroup usersData={assignedUsers} />
            </Box>
          )
        );
      }

      const isWidgetItem =
        dynamicFieldId === WidgetType.LOAN_NUMBER.id ||
        dynamicFieldId === WidgetType.MILESTONE_STATUS.id ||
        TAG_WIDGET_IDS.includes(dynamicFieldId);

      if (isWidgetItem) {
        return (
          <LoanInfoHeaderItem applicationId={applicationId} key={item.id} model={model} {...item} />
        );
      }

      const isAusRelatedField = ausRelatedFields.includes(normalizedName);

      const formattedValue = formatDynamicValue({
        value,
        type: dataType,
        field: normalizedName,
        dynamicFieldName,
      });

      const isAusLoading = getIsAusLoadingStatus({
        isAusSubmitting,
        field: normalizedName,
      });

      const isAusComparisonEnabled =
        withAusComparison && isAusRelatedField && !_.isEmpty(ausSummary);

      const ausComparisonProps = isAusComparisonEnabled
        ? {
            onMouseEnter,
            onMouseLeave,
            onClick: onAusFieldClick,
            'aria-describedby': ausComparisonPopperId,
          }
        : {};

      const itemBoxSx = isAusComparisonEnabled ? [itemStyles, ausFieldClickableStyles] : itemStyles;

      return (
        <Box key={item.id || name} sx={itemBoxSx} {...ausComparisonProps}>
          {shouldShowLabel && (
            <Typography
              color="secondary"
              data-testid={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_LOAN_INFO_HEADER_KEY_LABEL}
              sx={itemNameStyles}
              variant="regular14"
            >
              {name}
            </Typography>
          )}
          {isAusLoading || (isAusSwitching && isAusRelatedField) ? (
            <Skeleton variant="rectangular" width={30} />
          ) : (
            <HeaderFieldValue
              changeKind={changeKind}
              formattedValue={formattedValue}
              hasPendingChange={hasPendingChange}
              normalizedName={normalizedName}
              value={value}
            />
          )}
        </Box>
      );
    },
    [
      applicationId,
      assignedUsers,
      ausSummary,
      isAusSubmitting,
      isAusSwitching,
      model,
      onAusFieldClick,
      onMouseEnter,
      onMouseLeave,
    ],
  );

  const renderHeaderItemWithAus = useCallback(
    item => renderHeaderItem(item, { withAusComparison: true }),
    [renderHeaderItem],
  );

  const renderSecondaryHeaderItem = useCallback(
    item => (
      <LoanInfoHeaderItem applicationId={applicationId} key={item.id} model={model} {...item} />
    ),
    [applicationId, model],
  );

  const renderIdentityItem = useCallback(
    item => {
      const { dynamicFieldId, normalizedName, value, dataType, dynamicFieldName } = item;

      if (dynamicFieldId === WidgetType.LOAN_ROLES.id) {
        return assignedUsers?.length ? <PresenceAvatarGroup usersData={assignedUsers} /> : null;
      }

      if (
        dynamicFieldId === WidgetType.MILESTONE_STATUS.id ||
        normalizedName === STATUS_FIELD_NAME
      ) {
        return <MilestoneRoadmapWidget milestoneStatusName={milestoneStatusName ?? value} />;
      }

      if (TAG_WIDGET_IDS.includes(dynamicFieldId)) {
        return <LoanInfoHeaderItem applicationId={applicationId} model={model} {...item} />;
      }

      if (dynamicFieldId === WidgetType.LOAN_NUMBER.id || getIsLoanNumberField(item, loanNumber)) {
        return loanNumber ? (
          <Typography sx={identityLoanNumberStyles} variant="medium16">
            {`#${loanNumber}`}
          </Typography>
        ) : null;
      }

      const formattedValue = formatDynamicValue(
        { value, field: normalizedName, type: dataType, dynamicFieldName },
        { ...model },
      );

      return (
        <Box sx={identityItemStyles}>
          <Typography
            data-truncated
            className="truncate"
            sx={identityBorrowerStyles}
            variant="bold16"
          >
            {formattedValue}
          </Typography>
        </Box>
      );
    },
    [applicationId, assignedUsers, loanNumber, milestoneStatusName, model],
  );

  // Resolve identity content up front and drop empty items (e.g. an unassigned loan number) so the
  // divider index reflects what actually renders — otherwise a null leading item strands a divider.
  const renderedIdentityItems = identityItems
    .map(item => ({ item, content: renderIdentityItem(item) }))
    .filter(({ content }) => !!content);

  return (
    <Box sx={containerStyles}>
      {isHeaderViewLoading ? (
        <Box sx={identityRowStyles}>
          <Skeleton sx={identitySkeletonStyles} variant="text" width={220} />
        </Box>
      ) : (
        renderedIdentityItems.length > 0 && (
          <Box sx={identityRowStyles}>
            {renderedIdentityItems.map(({ item, content }, index) => (
              <Fragment key={item.id || item.name}>
                {index > 0 && (
                  <Divider flexItem orientation="vertical" sx={identityDividerStyles} />
                )}
                {content}
              </Fragment>
            ))}
          </Box>
        )
      )}

      <Box sx={metricsContainerStyles}>
        {!isHeaderViewLoading && metricColumns.length > 0 && (
          <Box aria-hidden ref={measureRef} sx={measureContainerStyles}>
            {metricColumns.map(column => (
              <Box key={column.columnIndex} sx={measureColumnSx}>
                {column.cells.map(cell => (
                  <HeaderGridCell
                    cell={cell}
                    getItemKey={getHeaderItemKey}
                    key={`${column.columnIndex}-${cell.rowIndex}`}
                    renderItem={renderHeaderItemWithAus}
                  />
                ))}
              </Box>
            ))}
          </Box>
        )}
        <Box
          data-testid={LoanHeadersTestIds.LOAN_APPLICATION_MAIN_HEADER_CONTENT}
          ref={visibleContainerRef}
          sx={metricsRowStyles}
          onMouseLeave={onResetTooltipState}
          onMouseOver={onMouseOver}
        >
          <TruncatedProvider />

          {(isHeaderViewLoading || _.isNil(primaryLoanHeaderView)) && (
            <Box sx={skeletonStyles}>
              <Skeleton variant="text" width="100%" />
            </Box>
          )}

          {!isHeaderViewLoading && (
            <HeaderGrid
              columnGap={gap}
              columns={displayedColumns}
              renderItem={renderHeaderItemWithAus}
            />
          )}
        </Box>
        {hasOverflow && (
          <Box sx={moreButtonWrapperStyles}>
            <MoreButtonCDS isSelected={!!overflowAnchorEl} onClick={onOpenOverflowPopover} />
          </Box>
        )}
      </Box>

      <PopoverCDS
        anchorEl={overflowAnchorEl}
        anchorOrigin={popoverAnchorOrigin}
        open={!!overflowAnchorEl}
        title="All Header Fields"
        transformOrigin={popoverTransformOrigin}
        onClose={onCloseOverflowPopover}
      >
        <Box sx={overflowPopoverContentStyles}>
          {flattenHeaderColumns(metricColumns).map(item => renderHeaderItemWithAus(item))}
        </Box>

        {isMobile && secondaryLoanHeaderView?.length > 0 && (
          <>
            <Divider sx={popoverDividerStyles} />
            <Box sx={detailedSummarySectionStyles}>
              <Typography sx={detailedSummaryTitleStyles} variant="regular14">
                Detailed Summary
              </Typography>
              <Box sx={detailedSummaryGridStyles}>
                {flattenHeaderColumns(secondaryLoanHeaderView).map(item =>
                  renderSecondaryHeaderItem(item),
                )}
              </Box>
            </Box>
          </>
        )}
      </PopoverCDS>
      {(!!anchorEl || isPopperHover) && (
        <ClickAwayListener onClickAway={onPopperClickAway}>
          <Popper
            open
            anchorEl={anchorEl}
            id={ausComparisonPopperId}
            modifiers={popperModifiers}
            placement="bottom-end"
            sx={popperStyles}
            onMouseEnter={onPopperEnter}
            onMouseLeave={onPopperLeave}
          >
            <AusComparison />
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

HeaderViewList.propTypes = {
  assignedUsers: PropTypes.arrayOf(PropTypes.object),
  secondaryLoanHeaderView: PropTypes.arrayOf(PropTypes.object),
};

export default HeaderViewList;
